// Prompt AD (2026-09-21) — Filters & Service "INSTALLED FILTERS & EQUIPMENT"
// con dos pestañas internas: [Installed Equipment] [Monthly Service].
import { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Wrench, CalendarClock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyEquip = { qty: 1, item: '', brand_model: '', notes: '' };
const emptyService = { item: '', frequency: '', next_service: '', last_service: '', notes: '' };

export default function FiltersServiceEquipmentCard({ job, onSaved }) {
  const [tab, setTab] = useState('equipment');
  const equipment = job.installed_equipment || [];
  const monthly = job.monthly_service || [];
  const [addingEq, setAddingEq] = useState(false);
  const [eqDraft, setEqDraft] = useState(emptyEquip);
  const [addingSvc, setAddingSvc] = useState(false);
  const [svcDraft, setSvcDraft] = useState(emptyService);
  const [saving, setSaving] = useState(false);

  const saveEquipment = async (list) => {
    setSaving(true);
    try {
      const res = await api.put(`/jobs/${job.id}`, { installed_equipment: list });
      onSaved(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const saveMonthly = async (list) => {
    setSaving(true);
    try {
      const res = await api.put(`/jobs/${job.id}`, { monthly_service: list });
      onSaved(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const addEquip = () => {
    if (!eqDraft.item.trim()) { toast.error('Item/Description requerido'); return; }
    saveEquipment([...equipment, { id: crypto.randomUUID(), ...eqDraft }]);
    setEqDraft(emptyEquip); setAddingEq(false);
  };
  const removeEquip = (idx) => saveEquipment(equipment.filter((_, i) => i !== idx));

  const addService = () => {
    if (!svcDraft.item.trim()) { toast.error('Item/Service requerido'); return; }
    saveMonthly([...monthly, { id: crypto.randomUUID(), ...svcDraft }]);
    setSvcDraft(emptyService); setAddingSvc(false);
  };
  const removeService = (idx) => saveMonthly(monthly.filter((_, i) => i !== idx));

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid="filters-equipment-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#3399FF]" /> Installed Filters &amp; Equipment
        </CardTitle>
        <div className="flex items-center gap-1 border-b border-gray-200 -mb-2 mt-1">
          {[{ key: 'equipment', label: 'Installed Equipment' }, { key: 'monthly', label: 'Monthly Service' }].map(t => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`filters-inner-tab-${t.key}`}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#003366] text-[#003366]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {tab === 'equipment' ? (
          <>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAddingEq(!addingEq)} data-testid="filters-equip-add-toggle">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {addingEq && (
              <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Input className="h-9 text-xs" type="number" placeholder="Qty" value={eqDraft.qty} onChange={e => setEqDraft({ ...eqDraft, qty: e.target.value })} data-testid="filters-equip-qty" />
                <Input className="h-9 text-xs sm:col-span-2" placeholder="Item / Description" value={eqDraft.item} onChange={e => setEqDraft({ ...eqDraft, item: e.target.value })} data-testid="filters-equip-item" />
                <Input className="h-9 text-xs" placeholder="Brand / Model" value={eqDraft.brand_model} onChange={e => setEqDraft({ ...eqDraft, brand_model: e.target.value })} data-testid="filters-equip-brand" />
                <Textarea className="text-xs sm:col-span-4" rows={2} placeholder="Notes" value={eqDraft.notes} onChange={e => setEqDraft({ ...eqDraft, notes: e.target.value })} data-testid="filters-equip-notes" />
                <div className="sm:col-span-4 flex justify-end gap-2">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setAddingEq(false); setEqDraft(emptyEquip); }}>Cancel</Button>
                  <Button size="sm" className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white" onClick={addEquip} disabled={saving} data-testid="filters-equip-save">Save</Button>
                </div>
              </div>
            )}
            {equipment.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No equipment installed yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="filters-equip-list">
                {equipment.map((eq, i) => (
                  <div key={eq.id || i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-slate-50 group" data-testid={`filters-equip-item-${i}`}>
                    <div className="text-xs text-gray-700">
                      <span className="font-medium">{eq.qty ? `${eq.qty}x ` : ''}{eq.item || eq.equipment_type_label}</span>
                      {eq.brand_model && <span className="text-gray-400"> — {eq.brand_model}</span>}
                      {eq.notes && <p className="text-[10px] text-gray-400">{eq.notes}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeEquip(i)} data-testid={`filters-equip-remove-${i}`}>
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAddingSvc(!addingSvc)} data-testid="filters-service-add-toggle">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
            {addingSvc && (
              <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100 grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Input className="h-9 text-xs sm:col-span-2" placeholder="Item / Service" value={svcDraft.item} onChange={e => setSvcDraft({ ...svcDraft, item: e.target.value })} data-testid="filters-svc-item" />
                <Input className="h-9 text-xs" placeholder="Frequency" value={svcDraft.frequency} onChange={e => setSvcDraft({ ...svcDraft, frequency: e.target.value })} data-testid="filters-svc-frequency" />
                <Input className="h-9 text-xs" type="date" value={svcDraft.next_service} onChange={e => setSvcDraft({ ...svcDraft, next_service: e.target.value })} data-testid="filters-svc-next" />
                <Input className="h-9 text-xs" type="date" value={svcDraft.last_service} onChange={e => setSvcDraft({ ...svcDraft, last_service: e.target.value })} data-testid="filters-svc-last" />
                <Textarea className="text-xs sm:col-span-5" rows={2} placeholder="Notes" value={svcDraft.notes} onChange={e => setSvcDraft({ ...svcDraft, notes: e.target.value })} data-testid="filters-svc-notes" />
                <div className="sm:col-span-5 flex justify-end gap-2">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setAddingSvc(false); setSvcDraft(emptyService); }}>Cancel</Button>
                  <Button size="sm" className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white" onClick={addService} disabled={saving} data-testid="filters-svc-save">Save</Button>
                </div>
              </div>
            )}
            {monthly.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No monthly service scheduled yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="filters-svc-list">
                {monthly.map((s, i) => (
                  <div key={s.id || i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-slate-50 group" data-testid={`filters-svc-row-${i}`}>
                    <div className="text-xs text-gray-700 flex items-center gap-2">
                      <CalendarClock className="w-3.5 h-3.5 text-violet-500" />
                      <div>
                        <span className="font-medium">{s.item}</span>{s.frequency ? ` · ${s.frequency}` : ''}
                        <p className="text-[10px] text-gray-400">Next: {s.next_service || '—'} · Last: {s.last_service || '—'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeService(i)} data-testid={`filters-svc-remove-${i}`}>
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
